import { Autocomplete, TextField, Chip, CircularProgress } from '@mui/material';
import { useEffect } from 'react';
import { useMenuDataCache } from '../../hooks/useMenuDataCache';

interface Bundle {
  bundleId: string;
  internalName: string;
  name?: string;
  [key: string]: any;
}

interface BundleSelectProps {
  value: Bundle | null | Bundle[];
  onChange: (bundle: Bundle | null | Bundle[]) => void;
  multiple?: boolean;
}

export function BundleSelect({ value, onChange, multiple = false }: BundleSelectProps) {
  const { data: menuData, isLoading: menuLoading, fetchMenuData } = useMenuDataCache();
  
  useEffect(() => {
    // Trigger cache loading - singleton pattern ensures this only fetches once
    fetchMenuData();
  }, [fetchMenuData]);

  const bundles = menuData?.bundles || [];

  // Helper to get display label for a bundle
  const getBundleLabel = (option: Bundle) => {
    if (!option) return 'No bundle';
    if (option.displayName) return option.displayName;
    if (option.internalName && option.bundleCode) return `${option.internalName} (${option.bundleCode})`;
    if (option.name && option.bundleCode) return `${option.name} (${option.bundleCode})`;
    return option.name || option.internalName || 'Unknown';
  };

  return (
    <Autocomplete
      options={Array.isArray(bundles) ? bundles : []}
      getOptionLabel={getBundleLabel}
      isOptionEqualToValue={(option, val) => {
        if (!option || !val) return false;
        return option.bundleId === (val as Bundle).bundleId;
      }}
      renderOption={(props, option) => {
        if (!option) return null;
        const label = getBundleLabel(option);
        return (
          <li {...props} key={option.bundleId || `bundle-${Math.random()}`}>
            {label}
          </li>
        );
      }}
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      multiple={multiple}
      renderTags={(tagValue, getTagProps) => {
        if (!tagValue || !Array.isArray(tagValue)) return [];
        return (tagValue as Bundle[]).map((option, index) => {
          if (!option) return null;
          const label = getBundleLabel(option);
          return (
            <Chip
              {...getTagProps({ index })}
              key={option.bundleId || `bundle-tag-${index}`}
              size="small"
              label={label}
            />
          );
        });
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Select Bundle"
          placeholder="Search for bundles..."
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {menuLoading && <CircularProgress size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
